import { useTranslation } from "react-i18next";
import * as React from "react";
import {
  formatCentsAsDollars,
  multiplyMoney,
  formatUnitOfMeasure,
} from "@/lib/price-utils";

interface PricingBreakdownSectionProps {
  data: {
    laborHours?: number | null;
    laborRate?: number | null;
    partsCostPremarkup?: number | null;
    partsCostPostmarkup?: number | null;
    partsAffectPrice?: boolean | null;
    markupPercent?: number | null;
    discountPercentage?: number | null;
    discountDescription?: string | null;
    amountPresalesTax?: number | null;
    amountPrediscount?: number | null;
    services?: Array<{ name: string; price: number }>;
    quoteServices?: any[] | null;
    products?: any[] | null;
    customParts?: Array<{ name: string; price: number }>;
    quoteCustomParts?: any[] | null;
  };
  hidePartDetails?: boolean; // When true, show only parts subtotal instead of individual line items
  isPricesLocked?: boolean; // When true, use stored partsCostPostmarkup instead of recalculating
  className?: string;
}

export const PricingBreakdownSection: React.FC<
  PricingBreakdownSectionProps
> = ({ data, hidePartDetails = false, isPricesLocked = false, className }) => {
  const { t } = useTranslation();
  return (
    <div className={`border border-slate-200 rounded-lg ${className || ""}`}>
      <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
        <h3 className="text-lg font-semibold text-slate-900">
          {t("features.quotes.quoteViewer.sections.pricingBreakdownSection.pricingDetails")}
        </h3>
      </div>

      <div className="p-6">
        <div className="space-y-4">
          {/* Labor Cost - only show if labor hours exist */}
          {data.laborHours != null && data.laborRate != null && (
            <>
              <div className="pt-4 border-t-2 border-slate-200">
                <div className="text-lg font-semibold text-slate-900 mb-3 pb-2 border-b border-slate-100">
                  {t("features.quotes.quoteViewer.sections.pricingBreakdownSection.labor")}
                </div>
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-slate-50">
                <div>
                  <div className="text-slate-800">{t("features.quotes.quoteViewer.sections.pricingBreakdownSection.laborCost")}</div>
                </div>
                <div className="text-slate-800">
                  ${formatCentsAsDollars(data.laborRate * data.laborHours)}
                </div>
              </div>
            </>
          )}

          {/* Parts Cost - only show if parts > 0 and affects price */}
          {data.partsAffectPrice !== false &&
            data.partsCostPremarkup != null &&
            data.partsCostPremarkup > 0 &&
            (!data.products || data.products.length === 0) &&
            (!data.quoteCustomParts || data.quoteCustomParts.length === 0) && (
              <>
                <div className="pt-4 border-t-2 border-slate-200">
                  <div className="text-lg font-semibold text-slate-900 mb-3 pb-2 border-b border-slate-100">
                    {t("features.quotes.quoteViewer.sections.pricingBreakdownSection.partsMaterials")}
                  </div>
                </div>
                <div className="flex justify-between items-center py-2.5 border-b border-slate-50">
                  <div>
                    <div className="text-slate-800">{t("features.quotes.quoteViewer.sections.pricingBreakdownSection.partsMaterialsTotal_2")}</div>
                  </div>
                  <div className="text-slate-800">
                    $
                    {formatCentsAsDollars(
                      data.partsCostPostmarkup || data.partsCostPremarkup,
                    )}
                  </div>
                </div>
              </>
            )}

          {/* Additional Services - handle both services and quoteServices arrays */}
          {((data.services?.length ?? 0) > 0 ||
            (data.quoteServices?.length ?? 0) > 0) && (
            <>
              <div className="pt-4 border-t-2 border-slate-200">
                <div className="text-lg font-semibold text-slate-900 mb-3 pb-2 border-b border-slate-100">
                  {t("common.services")}
                </div>
              </div>
              {(data.services || data.quoteServices || []).map(
                (service: any, index: number) => (
                  <div
                    key={index}
                    className="flex justify-between items-start py-2.5 border-b border-slate-50"
                  >
                    <div className="flex-1 mr-4">
                      <div className="text-slate-800 mb-1">
                        {service.name ||
                          service.title ||
                          `Service ${index + 1}`}
                      </div>
                      {service.description && (
                        <div className="text-xs text-slate-500">
                          {service.description}
                        </div>
                      )}
                    </div>
                    <div className="text-slate-800 text-right">
                      ${formatCentsAsDollars(service.price || 0)}
                    </div>
                  </div>
                ),
              )}
            </>
          )}

          {/* Parts & Materials - combined products and custom parts */}
          {data.partsAffectPrice !== false &&
            ((data.products && data.products.length > 0) ||
              (data.customParts?.length ?? 0) > 0 ||
              (data.quoteCustomParts?.length ?? 0) > 0) && (
              <>
                <div className="pt-4 border-t-2 border-slate-200">
                  <div className="text-lg font-semibold text-slate-900 mb-3 pb-2 border-b border-slate-100">
                    {t("features.quotes.quoteViewer.sections.pricingBreakdownSection.partsMaterials")}
                  </div>
                </div>
                {hidePartDetails ? (
                  // Show only subtotal when hidePartDetails is true
                  <div className="flex justify-between items-center py-2.5 border-b border-slate-50">
                    <div>
                      <div className="text-slate-800">
                        {t("features.quotes.quoteViewer.sections.pricingBreakdownSection.partsMaterialsTotal")}
                      </div>
                      <div className="text-xs text-slate-500">
                        {(() => {
                          const productCount = data.products?.length || 0;
                          const customPartCount =
                            (data.customParts?.length || 0) +
                            (data.quoteCustomParts?.length || 0);
                          const totalItems = productCount + customPartCount;
                          return totalItems > 0
                            ? `${totalItems} item${totalItems !== 1 ? "s" : ""}`
                            : "";
                        })()}
                      </div>
                    </div>
                    <div className="text-slate-800">
                      $
                      {formatCentsAsDollars(
                        (() => {
                          // For locked quotes (sent for approval): use stored partsCostPostmarkup
                          // This preserves the historical pricing snapshot without recalculation
                          if (
                            isPricesLocked &&
                            data.partsCostPostmarkup != null
                          ) {
                            return data.partsCostPostmarkup;
                          }

                          // For draft quotes: recalculate from individual product prices (live pricing)
                          const productsTotal = (data.products || []).reduce(
                            (sum: number, product: any) =>
                              sum + (product.postMarkupTotalPrice || 0),
                            0,
                          );
                          const customPartsTotal = (
                            data.customParts ||
                            data.quoteCustomParts ||
                            []
                          ).reduce(
                            (sum: number, customPart: any) =>
                              sum +
                              (customPart.postMarkupTotalPrice ||
                                customPart.postMarkupPrice ||
                                0),
                            0,
                          );
                          return productsTotal + customPartsTotal;
                        })(),
                      )}
                    </div>
                  </div>
                ) : (
                  // Show detailed breakdown when hidePartDetails is false (default)
                  <>
                    {/* Hajoca Products */}
                    {data.products &&
                      data.products.map((product: any, index: number) => (
                        <div
                          key={`product-${index}`}
                          className="flex justify-between items-start py-2.5 border-b border-slate-50"
                        >
                          <div className="flex-1 mr-4">
                            <div className="text-slate-800 mb-1">
                              {product.name ||
                                product.description ||
                                `Product ${index + 1}`}
                            </div>
                            <div className="text-xs text-slate-500 leading-relaxed">
                              {t("features.quotes.quoteViewer.sections.pricingBreakdownSection.quantity")} {product.quantity || 1}
                              {product.postMarkupUnitPrice > 0 && (
                                <span className="ml-2">
                                  @ $
                                  {formatCentsAsDollars(
                                    product.postMarkupUnitPrice,
                                  )}{" "}
                                  {formatUnitOfMeasure(product.unitOfMeasure)}
                                </span>
                              )}
                              {product.hajocaProductId && (
                                <span className="ml-2 text-slate-400">
                                  (#{product.hajocaProductId})
                                </span>
                              )}
                            </div>
                            {product.notes && (
                              <div className="text-xs text-slate-400 mt-1 italic">
                                {product.notes}
                              </div>
                            )}
                          </div>
                          <div className="text-slate-800 text-right">
                            $
                            {formatCentsAsDollars(
                              product.postMarkupTotalPrice || 0,
                            )}
                          </div>
                        </div>
                      ))}
                    {/* Custom Parts */}
                    {(data.customParts || data.quoteCustomParts || []).map(
                      (customPart: any, index: number) => (
                        <div
                          key={`custom-${index}`}
                          className="flex justify-between items-start py-2.5 border-b border-slate-50"
                        >
                          <div className="flex-1 mr-4">
                            <div className="text-slate-800 mb-1">
                              {customPart.name ||
                                customPart.title ||
                                `Custom Part ${index + 1}`}
                            </div>
                            <div className="text-xs text-slate-500 leading-relaxed">
                              {t("features.quotes.quoteViewer.sections.pricingBreakdownSection.quantity")} {customPart.quantity || 1}
                              {customPart.postMarkupUnitPrice && (
                                <span className="ml-2">
                                  @ $
                                  {formatCentsAsDollars(
                                    customPart.postMarkupUnitPrice,
                                  )}{" "}
                                  {formatUnitOfMeasure(null, true)}
                                </span>
                              )}
                            </div>
                            {customPart.notes && (
                              <div className="text-xs text-slate-400 mt-1 italic">
                                {customPart.notes}
                              </div>
                            )}
                          </div>
                          <div className="text-slate-800 text-right">
                            $
                            {formatCentsAsDollars(
                              customPart.postMarkupTotalPrice ||
                                customPart.postMarkupPrice ||
                                0,
                            )}
                          </div>
                        </div>
                      ),
                    )}
                  </>
                )}
              </>
            )}

          {/* Discount - only show if discount > 0 */}
          {(data.discountPercentage || 0) > 0 && (
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <div>
                <div className="font-medium text-green-700">
                  {t("features.quotes.quoteViewer.sections.pricingBreakdownSection.discount")}{data.discountPercentage}{t("features.quotes.quoteViewer.sections.pricingBreakdownSection.off")}
                </div>
                <div className="text-sm text-slate-600">
                  {data.discountDescription
                    ? `Discount reason: ${data.discountDescription}`
                    : t("features.quotes.quoteViewer.sections.pricingBreakdownSection.appliedToTotal")}
                </div>
              </div>
              <div className="font-medium text-green-700">
                -$
                {(() => {
                  let prediscountAmount = data.amountPrediscount || 0;

                  // Fallback calculation for templates without amountPrediscount
                  if (prediscountAmount === 0) {
                    const laborCost =
                      (data.laborHours || 0) * (data.laborRate || 0);
                    // Calculate parts cost from products if available, otherwise use partsCostPremarkup
                    let partsCost = 0;
                    if (data.products && data.products.length > 0) {
                      partsCost = data.products.reduce(
                        (sum: number, product: any) =>
                          sum + (product.postMarkupTotalPrice || 0),
                        0,
                      );
                    } else if (data.partsAffectPrice) {
                      partsCost = multiplyMoney(
                        data.partsCostPremarkup || 0,
                        1 + (data.markupPercent || 0) / 100,
                      );
                    }
                    const servicesCost = (
                      data.services ||
                      data.quoteServices ||
                      []
                    ).reduce(
                      (sum: number, service: any) => sum + (service.price || 0),
                      0,
                    );
                    const customPartsCost =
                      data.partsAffectPrice !== false
                        ? (
                            data.customParts ||
                            data.quoteCustomParts ||
                            []
                          ).reduce(
                            (sum: number, customPart: any) =>
                              sum +
                              (customPart.postMarkupTotalPrice ||
                                customPart.postMarkupPrice ||
                                0),
                            0,
                          )
                        : 0;
                    prediscountAmount =
                      laborCost + partsCost + servicesCost + customPartsCost;
                  }

                  const discountPercent = data.discountPercentage || 0;
                  const discountAmount = multiplyMoney(
                    prediscountAmount,
                    discountPercent / 100,
                  );
                  return formatCentsAsDollars(discountAmount);
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
